import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Calendar, MapPin, Clock, AlertTriangle } from "lucide-react";
import { usePageMeta } from "@/utils/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SV_WEEKDAYS = ["Söndag", "Måndag", "Tisdag", "Onsdag", "Torsdag", "Fredag", "Lördag"];
const SV_MONTHS = ["januari", "februari", "mars", "april", "maj", "juni", "juli", "augusti", "september", "oktober", "november", "december"];

function formatSwedishDate(iso) {
  // iso = "2026-04-30"
  try {
    const [y, m, d] = iso.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    return `${SV_WEEKDAYS[dt.getUTCDay()]} ${d} ${SV_MONTHS[m - 1]}`;
  } catch {
    return iso;
  }
}

export default function VeckansHandelser() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/evenemang/veckans`)
      .then(res => setData(res.data))
      .catch(() => setError("Kunde inte hämta veckans händelser. Försök igen senare."));
  }, []);

  usePageMeta({
    title: "Veckans händelser – kommande sju dagar | vidalv.live",
    description: "Lokala evenemang i Backe, Rossön och bygderna vid Fjällsjöälven de kommande sju dagarna. Marknader, möten, konserter och mer.",
    path: "/veckans-handelser",
  });

  if (error) {
    return (
      <div data-testid="veckans-error" className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
        <p className="text-falu">{error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div data-testid="veckans-loading" className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
        <p className="text-bark">Läser in…</p>
      </div>
    );
  }

  return (
    <div data-testid="veckans-handelser-page" className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <p className="font-body text-xs font-semibold text-bark/50 uppercase tracking-[0.2em] mb-1">Veckans händelser</p>
      <h1 className="font-fraktur-smooth text-2xl sm:text-3xl text-falu tracking-wide mb-3">Vad händer här denna vecka?</h1>
      <p className="text-sm text-bark mb-8 leading-relaxed">
        Publicerade evenemang i bygden de kommande sju dagarna ({data.from_date} till {data.to_date}).
        {" "}<Link to="/skicka-evenemang" className="text-falu underline" data-testid="link-skicka-evenemang">Tipsa redaktionen</Link> om något du vet händer.
      </p>

      {data.count === 0 ? (
        <div data-testid="veckans-empty" className="bg-bone-alt border border-warm-border rounded-sm p-6 sm:p-8">
          <Calendar className="text-pine mb-3" size={32} />
          <p className="text-ink mb-2 font-semibold">Inga evenemang är publicerade för den kommande veckan ännu.</p>
          <p className="text-sm text-bark mb-4">Vet du om något? <Link to="/skicka-evenemang" className="text-falu underline">Tipsa gärna redaktionen</Link> – vi lägger gärna till.</p>
        </div>
      ) : (
        <ul data-testid="veckans-list" className="flex flex-col gap-4">
          {data.items.map(ev => (
            <li key={ev.id} data-testid={`veckans-item-${ev.id}`} className="bg-white border border-warm-border rounded-sm p-4 sm:p-5 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 mb-2">
                <Link to={`/evenemang/${ev.id}`} className="font-heading text-lg sm:text-xl text-ink hover:text-falu no-underline">
                  {ev.titel}
                </Link>
                <span className="text-xs text-bark/70 uppercase tracking-wide">
                  {formatSwedishDate(ev.datum)}
                </span>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-bark mb-2">
                {ev.tid && (
                  <span className="inline-flex items-center gap-1"><Clock size={14} /> {ev.tid}</span>
                )}
                {ev.plats && (
                  <span className="inline-flex items-center gap-1"><MapPin size={14} /> {ev.plats}</span>
                )}
                {ev.arrangor && (
                  <span className="text-bark/70">· {ev.arrangor}</span>
                )}
              </div>
              {(ev.kortbeskrivning || ev.beskrivning) && (
                <p className="text-sm text-ink/80 leading-relaxed line-clamp-3">{ev.kortbeskrivning || ev.beskrivning}</p>
              )}
            </li>
          ))}
        </ul>
      )}

      {data.count > 0 && (
        <div data-testid="veckans-trust-notice" className="mt-6 bg-bone-alt/60 border-l-3 border-falu/60 rounded-sm p-4 flex gap-3 items-start">
          <AlertTriangle size={16} className="text-falu shrink-0 mt-0.5" />
          <p className="text-xs text-bark leading-relaxed">
            <strong>Uppgifter kan ändras.</strong> Kontrollera alltid tid, plats och kontaktuppgifter hos arrangören innan du åker.
          </p>
        </div>
      )}

      <p className="text-xs text-bark/60 mt-8">
        Uppdaterad {new Date(data.generated_at).toLocaleString("sv-SE")}. Redaktionen får veckorapport automatiskt varje måndag.
      </p>
    </div>
  );
}
